import {
  GlobalOrderTypeCode,
  OrderFulfillmentType,
  OrderSource,
  OrderType,
} from "~/entities/orderEntities";

/**
 * StoreFinder exposes a simplified Pickup / Delivery toggle. This maps that UI
 * value to the underlying order-type fulfilment codes and resolves the matching
 * active order type for a given site + order source.
 *
 * Notes:
 *  - "PICKUP" in the finder corresponds to the COLLECTION fulfilment type
 *    (pickup / takeaway). IN_STORE (dine-in) is intentionally not matched here —
 *    the finder is a pickup/delivery surface.
 *  - The match is scoped to the current order source so e.g. table-ordering order
 *    types aren't picked while browsing online, mirroring ChangeOrderModal's
 *    `orderTypeOptions` filter.
 */
export type StoreFinderFulfilment = "PICKUP" | "DELIVERY";

const fulfilmentTypeFor = (
  fulfilment: StoreFinderFulfilment,
): OrderFulfillmentType =>
  fulfilment === "DELIVERY"
    ? OrderFulfillmentType.DELIVERY
    : OrderFulfillmentType.COLLECTION;

/** Resolve the active order type matching a finder fulfilment for a site. */
export const findOrderTypeForFulfilment = (
  orderTypes: OrderType[],
  fulfilment: StoreFinderFulfilment,
  orderSource: OrderSource,
): OrderType | undefined => {
  const wanted = fulfilmentTypeFor(fulfilment);
  const matches = orderTypes.filter(
    (o) => o.active && o.fulfillmentType === wanted && o.source === orderSource,
  );
  if (matches.length <= 1) return matches[0];
  // "No multiple order types": when several qualify, pick one canonical type
  // deterministically — prefer a well-known global code, else the first match.
  const globalCodes = new Set<string>(Object.values(GlobalOrderTypeCode));
  return matches.find((o) => globalCodes.has(o.code)) ?? matches[0];
};

/**
 * Derive the StoreFinder's initial fulfilment toggle from the current order type.
 * Delivery stays delivery; everything else (collection / in-store / none) opens
 * on Pickup.
 */
export const fulfilmentFromOrderType = (
  orderType: OrderType | undefined,
): StoreFinderFulfilment =>
  orderType?.fulfillmentType === OrderFulfillmentType.DELIVERY
    ? "DELIVERY"
    : "PICKUP";
